// src/components/reviews/components/ReviewCard.jsx
import React from 'react';
import { Star, User, MessageSquare, Trash2, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// shadcn components
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';

/**
 * Review Card - Displays a single review with reply functionality
 */
const ReviewCard = ({ review, onReplyClick, onDeleteReply, isReplying, isDeletingReply }) => {
  // Parse dates
  const reviewDate = review.createTime ? new Date(review.createTime) : null;
  const replyDate = review.reviewReply?.updateTime ? new Date(review.reviewReply.updateTime) : null;

  // Render star rating
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating
            ? 'text-yellow-500 fill-yellow-500'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Reviewer Info */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                {review.reviewer?.profilePhotoUrl ? (
                  <img
                    src={review.reviewer.profilePhotoUrl}
                    alt={review.reviewer.displayName}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <User className="h-5 w-5" />
                )}
              </div>

              {/* Name & Date */}
              <div>
                <p className="font-medium">
                  {review.reviewer?.displayName || 'Anonymous'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-0.5">
                    {renderStars(review.starRating || 0)}
                  </div>
                  {reviewDate && (
                    <>
                      <span className="text-gray-400">•</span>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDistanceToNow(reviewDate, { addSuffix: true })}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Reply Button */}
            {!review.reviewReply && (
              <Button
                onClick={() => onReplyClick(review)}
                disabled={isReplying}
                size="sm"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {isReplying ? 'Replying...' : 'Reply'}
              </Button>
            )}
          </div>

          {/* Review Text */}
          {review.comment && (
            <div className="pl-13">
              <p className="text-gray-700">{review.comment}</p>
            </div>
          )}

          {/* Business Reply */}
          {review.reviewReply && (
            <div className="pl-13 ml-2 border-l-2 border-blue-200 bg-blue-50 p-4 rounded-r-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  <p className="text-sm font-medium text-blue-900">Business Response</p>
                  {replyDate && (
                    <>
                      <span className="text-gray-400">•</span>
                      <p className="text-xs text-gray-600">
                        {formatDistanceToNow(replyDate, { addSuffix: true })}
                      </p>
                    </>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteReply(review)}
                  disabled={isDeletingReply}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-gray-700 text-sm">{review.reviewReply.comment}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewCard;
